// annotate — dessiner sur une capture pour montrer au lieu de décrire.
//
// ── CE QUE ÇA DONNE À L'OPÉRATEUR ───────────────────────────────────────────
//
// Sa demande, le 2026-10-04 : « enable user to click on the uploaded photos or screen
// captures and draw on them ». Il entoure le bouton qui ne va pas ; l'agent voit ce qu'on
// lui montre du doigt au lieu de le deviner.
//
// ── LA DÉCISION QUI TIENT TOUT : LES TRAITS SONT DES DONNÉES ────────────────
//
// Pas des pixels. Un trait est une liste de points dans le repère de L'IMAGE, et l'image
// annotée est REDESSINÉE depuis l'originale à chaque changement. Trois conséquences, et
// chacune évite un défaut réel :
//
//   • ANNULER EST EXACT — on retire un trait de la liste. L'autre façon, repeindre
//     par-dessus en couleur de fond, laisse des traces et ne rend jamais l'image d'avant.
//   • CORRIGER DIX FOIS NE DÉGRADE RIEN. Sans ça, chaque enregistrement ré-encoderait une
//     image déjà ré-encodée : le fournisseur prévient que « heavy JPEG compression can make
//     text difficult to read », et c'est le texte de l'app qu'on cherche à montrer.
//   • LA GÉOMÉTRIE SE TESTE SANS NAVIGATEUR — et c'est elle qui se trompe en silence.
//
// ── POURQUOI AUCUNE LIBRAIRIE ───────────────────────────────────────────────
//
// Pointer Events couvre souris, doigt et stylet d'un seul jeu d'événements. Données de
// compatibilité MDN, lues à la source le 2026-10-04 : Chrome 55, Edge 12, Firefox 59,
// Safari 13, Chrome Android 55, Safari iOS 13, Samsung 6.0, Firefox Android 79. Rien à
// installer, et rien que les utilisateurs des apps auraient à télécharger.
//
// Tout ce fichier est PUR.

export type Point = { readonly x: number; readonly y: number };
export type Rect = { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
export type Size = { readonly width: number; readonly height: number };

/** Un trait : des points dans le repère de l'image, sa couleur, son épaisseur. */
export type Stroke = {
  readonly points: readonly Point[];
  readonly color: string;
  readonly width: number;
};

/** Les couleurs proposées. La PREMIÈRE compte le plus : l'opérateur dessine sans choisir, et
 *  un trait gris sur une interface grise n'annote rien. */
export const ANNOTATE_COLORS = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#111827"] as const;

/** L'épaisseur telle qu'elle doit PARAÎTRE à l'écran, en pixels CSS. */
export const ANNOTATE_WIDTH_CSS_PX = 4;

/** En dessous, un point n'ajoute rien au tracé.
 *
 *  Un glissement lent produit un point par image d'écran : sans ce seuil, un seul geste en
 *  stocke des milliers, et chaque trait suivant redessine le tout. */
export const MIN_POINT_DISTANCE_PX = 2;

const fini = (n: number): boolean => Number.isFinite(n);

/** Un trait vide, prêt à recevoir des points. */
export const newStroke = (color: string, width: number): Stroke => ({ points: [], color, width });

/**
 * PURE. Un point de l'écran ramené aux pixels de l'image.
 *
 * LES DEUX AXES SÉPARÉMENT : une boîte qui ne respecte pas les proportions de l'image
 * (`object-fit: fill`) a deux facteurs différents, et un seul facteur décalerait tout le
 * tracé sur l'un des axes.
 *
 * HORS DE LA BOÎTE, ON RAMÈNE DEDANS plutôt qu'on ignore : le doigt qui sort de l'image et
 * revient doit tracer un trait continu le long du bord, pas deux morceaux séparés par un
 * saut que personne n'a dessiné.
 */
export function toImagePoint(client: Point, shown: Rect, image: Size): Point | null {
  for (const n of [client.x, client.y, shown.x, shown.y, shown.width, shown.height, image.width, image.height]) {
    if (!fini(n)) return null;
  }
  if (shown.width <= 0 || shown.height <= 0 || image.width <= 0 || image.height <= 0) return null;
  const fx = image.width / shown.width;
  const fy = image.height / shown.height;
  const x = Math.round((client.x - shown.x) * fx);
  const y = Math.round((client.y - shown.y) * fy);
  return {
    x: Math.min(image.width, Math.max(0, x)),
    y: Math.min(image.height, Math.max(0, y)),
  };
}

/**
 * PURE. Le trait avec ce point en plus — ou le trait REÇU, inchangé, si le point n'apporte
 * rien.
 *
 * Rendre la même référence quand rien ne change laisse l'appelant éviter un rendu : c'est
 * ce qui fait qu'un glissement reste fluide au lieu de redessiner trente fois par seconde.
 */
export function appendPoint(stroke: Stroke, p: Point): Stroke {
  if (!fini(p.x) || !fini(p.y)) return stroke;
  const dernier = stroke.points[stroke.points.length - 1];
  if (dernier !== undefined) {
    const dx = p.x - dernier.x, dy = p.y - dernier.y;
    // EN DIAGONALE, pas axe par axe : deux points éloignés de 2 en x ET en y sont à 2,83
    // l'un de l'autre, donc au-delà du seuil. Mesurer par axe les écarterait à tort.
    if (dx * dx + dy * dy < MIN_POINT_DISTANCE_PX * MIN_POINT_DISTANCE_PX) return stroke;
  }
  return { ...stroke, points: [...stroke.points, p] };
}

/** PURE. Le dernier trait retiré. Sur une liste vide, LA MÊME liste — pour que l'appelant
 *  n'ait pas à se demander s'il doit redessiner. */
export function undoStroke(strokes: readonly Stroke[]): readonly Stroke[] {
  if (strokes.length === 0) return strokes;
  return strokes.slice(0, -1);
}

/**
 * PURE. L'épaisseur à employer DANS l'image pour qu'un trait paraisse constant à l'écran.
 *
 * Une image de 1024 px affichée sur 512 demande un trait deux fois plus épais : sans ce
 * facteur, l'opérateur dessinerait un trait deux fois plus fin que celui qu'il voit sous son
 * doigt. Jamais sous 1 pixel — un trait invisible n'annote rien.
 */
export function strokeWidthFor(shown: Rect, image: Size): number {
  if (!fini(shown.width) || !fini(image.width) || shown.width <= 0 || image.width <= 0) {
    return ANNOTATE_WIDTH_CSS_PX;
  }
  return Math.max(1, Math.round(ANNOTATE_WIDTH_CSS_PX * (image.width / shown.width)));
}
